import React, { useState } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import { SignupRequestDTO } from '../../../types/authentication.dto';
import PasswordCriteriaList from '../PasswordCriteriaList.component';
import { validatePassword } from '../password-rules.util';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Props = {
  control: Control<SignupRequestDTO>;
  watch: UseFormWatch<SignupRequestDTO>;
  setValue: UseFormSetValue<SignupRequestDTO>;
};

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  row: { display: 'flex', gap: theme.spacing(2) },
  toggle: { minWidth: 44, minHeight: 44 },
}));

const IdentityStep: React.FC<Props> = ({ control, watch }) => {
  const classes = useStyles();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const password = watch('password');

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <Controller
          name="firstname"
          control={control}
          rules={{ required: 'Veuillez saisir votre prénom' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              inputRef={field.ref}
              autoFocus
              label="Prénom"
              autoComplete="given-name"
              variant="outlined"
              fullWidth
              error={Boolean(error)}
              helperText={error?.message}
              inputProps={{ name: 'firstname', 'aria-invalid': Boolean(error) }}
            />
          )}
        />
        <Controller
          name="lastname"
          control={control}
          rules={{ required: 'Veuillez saisir votre nom' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              inputRef={field.ref}
              label="Nom"
              autoComplete="family-name"
              variant="outlined"
              fullWidth
              error={Boolean(error)}
              helperText={error?.message}
              inputProps={{ name: 'lastname', 'aria-invalid': Boolean(error) }}
            />
          )}
        />
      </div>

      <Controller
        name="email"
        control={control}
        rules={{
          required: 'Veuillez saisir votre adresse mail',
          pattern: { value: EMAIL_PATTERN, message: "L'adresse mail n'est pas valide" },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            label="Adresse mail"
            type="email"
            autoComplete="email"
            variant="outlined"
            fullWidth
            error={Boolean(error)}
            helperText={error?.message}
            inputProps={{ name: 'email', 'aria-invalid': Boolean(error) }}
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        rules={{
          required: 'Veuillez saisir un mot de passe',
          validate: (value) => validatePassword(value),
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            label="Mot de passe"
            type={isPasswordVisible ? 'text' : 'password'}
            autoComplete="new-password"
            variant="outlined"
            fullWidth
            error={Boolean(error)}
            helperText={error?.message}
            inputProps={{ name: 'password', 'aria-invalid': Boolean(error) }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.toggle}
                    aria-label={
                      isPasswordVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
                    }
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  >
                    {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <PasswordCriteriaList password={password} />
    </div>
  );
};

export default IdentityStep;
